import { type PatchOperation, stringify } from "@medplum/core";
import type { Address, ContactPoint, HumanName, Identifier, Patient } from "@medplum/fhirtypes";
import { stripUSPhonePrefix, toE164PhoneNumber } from "@/lib/utils/phone/phone-utils";
import {
  type ExternalReference,
  newExternalReferences,
  readExternalReferences,
  toExternalReferenceIdentifier,
} from "@/models/base/external-reference";
import { MEMBER_IDENTIFIER } from "./member-identifiers";

/**
 * Pure builders for partner-written patients (API v2 `POST /patients`, `PATCH /patients/{id}`).
 *
 * The write paths live on `Member` (`createExternal`, `patchExternal`); these functions only shape
 * FHIR. The stored shape matches what the chart path writes, so a partner-created patient reads
 * like any other: the email identifier plus email telecom, one official name, and the practice as
 * `managingOrganization`. The compartment is left to the caller's AccessPolicy.
 */

/**
 * Marks a Patient the API serving identity created. Only such a patient may have its practice
 * Account completed on a retry: its Account was created under the same policy, so the conditional
 * create finds it. An app-created Account can sit outside the practice compartment, where that
 * search would miss it and write a duplicate. Internal namespace, so export cleaning drops it.
 */
export const PARTNER_CREATED_TAG = {
  system: "https://ultralighthealth.com/fhir/api-v2/origin",
  code: "partner",
} as const;

export type ExternalPatientGender = NonNullable<Patient["gender"]>;

export interface ExternalPatientAddress {
  lines?: string[];
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface ExternalPatientCreate {
  practiceId: string;
  externalRef: ExternalReference;
  givenName: string;
  familyName: string;
  email: string;
  birthDate?: string;
  gender?: ExternalPatientGender;
  phone?: string;
  address?: ExternalPatientAddress;
}

/** Merge-patch changes: absent leaves a field alone, `null` clears it. */
export interface ExternalPatientChanges {
  givenName?: string;
  familyName?: string;
  birthDate?: string | null;
  gender?: ExternalPatientGender;
  phone?: string | null;
  address?: {
    lines?: string[] | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  appendExternalRefs?: ExternalReference[];
}

/** Published field names, for the change log. Never the values. `email` is written on create only. */
export type ExternalPatientField =
  | "name"
  | "email"
  | "birth_date"
  | "sex"
  | "phone"
  | "address"
  | "external_refs";

export interface ExternalPatientUpdate {
  operations: PatchOperation[];
  changedFields: ExternalPatientField[];
}

/** Stored phone form, same as the chart path: E.164 with a US `+1` stripped. Undefined when invalid. */
export function normalizeExternalPhone(phone: string): string | undefined {
  const e164 = toE164PhoneNumber(phone);
  return e164 ? stripUSPhonePrefix(e164) : undefined;
}

/** Email identities are stored and compared trimmed and lowercased (a machine key, so no locale). */
export function normalizeMemberEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function toFhirExternalPatient(data: ExternalPatientCreate): Patient {
  const email = normalizeMemberEmail(data.email);
  const phone = data.phone ? normalizeExternalPhone(data.phone) : undefined;
  const address = toFhirAddress(data.address);
  return {
    resourceType: "Patient",
    meta: { tag: [{ ...PARTNER_CREATED_TAG }] },
    identifier: [
      { system: MEMBER_IDENTIFIER.Email, value: email },
      toExternalReferenceIdentifier(data.externalRef),
    ],
    name: [{ use: "official", given: [data.givenName], family: data.familyName }],
    telecom: [
      { system: "email", value: email },
      ...(phone ? [{ system: "phone", value: phone } satisfies ContactPoint] : []),
    ],
    ...(data.birthDate ? { birthDate: data.birthDate } : {}),
    ...(data.gender ? { gender: data.gender } : {}),
    ...(address ? { address: [address] } : {}),
    managingOrganization: { reference: `Organization/${data.practiceId}` },
  };
}

export function isPartnerCreated(patient: Patient): boolean {
  return !!patient.meta?.tag?.some(
    (tag) => tag.system === PARTNER_CREATED_TAG.system && tag.code === PARTNER_CREATED_TAG.code,
  );
}

/**
 * JSON patch operations that move `current` to the requested state, plus the published names of
 * the fields that actually change. A field whose next value equals the stored one produces no
 * operation. Both sides go through `stringify`, which drops empty values the way Medplum's
 * serializer does, so a request that restates stored data is a no-op.
 */
export function buildExternalPatientUpdate(
  current: Patient,
  changes: ExternalPatientChanges,
): ExternalPatientUpdate {
  const candidates: { field: ExternalPatientField; path: keyof Patient; next: unknown }[] = [
    { field: "name", path: "name", next: nextNames(current.name, changes) },
    {
      field: "birth_date",
      path: "birthDate",
      next: changes.birthDate === undefined ? current.birthDate : (changes.birthDate ?? undefined),
    },
    { field: "sex", path: "gender", next: changes.gender ?? current.gender },
    { field: "phone", path: "telecom", next: nextTelecom(current.telecom, changes.phone) },
    { field: "address", path: "address", next: nextAddresses(current.address, changes.address) },
    {
      field: "external_refs",
      path: "identifier",
      next: nextIdentifiers(current.identifier, changes.appendExternalRefs),
    },
  ];

  const operations: PatchOperation[] = [];
  const changedFields: ExternalPatientField[] = [];
  for (const { field, path, next } of candidates) {
    const stored = current[path];
    if (normalized(next) === normalized(stored)) continue;
    changedFields.push(field);
    operations.push(toOperation(`/${path}`, stored, next));
  }
  return { operations, changedFields };
}

//----------------------------------------------------------------------------------------------
//                                         HELPERS
//----------------------------------------------------------------------------------------------

function normalized(value: unknown): string {
  return stringify(value) ?? "";
}

function isEmpty(value: unknown): boolean {
  return normalized(value) === "" || normalized(value) === "[]" || normalized(value) === "{}";
}

function toOperation(path: string, stored: unknown, next: unknown): PatchOperation {
  if (isEmpty(next)) return { op: "remove", path };
  return { op: isEmpty(stored) ? "add" : "replace", path, value: next };
}

function toFhirAddress(address: ExternalPatientAddress | undefined): Address | undefined {
  if (!address) return undefined;
  const fhir: Address = {
    ...(address.lines?.length ? { line: address.lines } : {}),
    ...(address.city ? { city: address.city } : {}),
    ...(address.state ? { state: address.state } : {}),
    ...(address.postalCode ? { postalCode: address.postalCode } : {}),
  };
  return Object.keys(fhir).length ? fhir : undefined;
}

/**
 * The published name is the legal (official, else unnamed, else first) entry: `given[0]` and
 * `family`. A change replaces those two parts on that entry and keeps middle names. `text` goes,
 * because it would keep displaying the old name.
 */
function nextNames(
  names: HumanName[] | undefined,
  changes: ExternalPatientChanges,
): HumanName[] | undefined {
  const existing = names ?? [];
  const index = primaryNameIndex(existing);
  const primary: HumanName = index < 0 ? { use: "official" } : existing[index];
  const [storedGiven, ...middleNames] = primary.given ?? [];
  const given = changes.givenName ?? storedGiven;
  const family = changes.familyName ?? primary.family;
  // restating the stored parts is a no-op, so `text` survives it
  if (given === storedGiven && family === primary.family) return names;
  const { text: _text, given: _given, family: _family, ...rest } = primary;
  const next: HumanName = {
    ...rest,
    ...(given ? { given: [given, ...middleNames] } : {}),
    ...(family ? { family } : {}),
  };
  if (index < 0) return [...existing, next];
  return existing.map((name, position) => (position === index ? next : name));
}

/** Mirrors `getPrimaryHumanName`: official, else unnamed-use, else the first entry. */
function primaryNameIndex(names: HumanName[]): number {
  const official = names.findIndex((name) => name.use === "official");
  if (official >= 0) return official;
  const unnamed = names.findIndex((name) => !name.use);
  if (unnamed >= 0) return unnamed;
  return names.length ? 0 : -1;
}

/** `Member.phone` reads the first phone entry, so that is the one a change replaces. */
function nextTelecom(
  telecom: ContactPoint[] | undefined,
  phone: string | null | undefined,
): ContactPoint[] | undefined {
  if (phone === undefined) return telecom;
  const existing = telecom ?? [];
  const index = existing.findIndex((entry) => entry.system === "phone");
  if (phone === null) return existing.filter((entry) => entry.system !== "phone");
  const value = normalizeExternalPhone(phone);
  if (!value) throw new Error("Phone must be validated before it reaches the patch builder.");
  if (index < 0) return [...existing, { system: "phone", value }];
  return existing.map((entry, position) => (position === index ? { ...entry, value } : entry));
}

/**
 * `Member.address` reads `address[0]`; nested fields merge. Clearing removes every stored address,
 * because removing only the first would publish the next one in its place.
 */
function nextAddresses(
  addresses: Address[] | undefined,
  changes: ExternalPatientChanges["address"],
): Address[] | undefined {
  if (changes === undefined) return addresses;
  if (changes === null) return undefined;
  const [first, ...others] = addresses ?? [];
  const merged: Address = { ...(first ?? {}) };
  applyAddressField(merged, "line", changes.lines);
  applyAddressField(merged, "city", changes.city);
  applyAddressField(merged, "state", changes.state);
  applyAddressField(merged, "postalCode", changes.postalCode);
  return isEmpty(merged) ? undefined : [merged, ...others];
}

function applyAddressField<K extends "line" | "city" | "state" | "postalCode">(
  address: Address,
  key: K,
  value: Address[K] | null | undefined,
): void {
  if (value === undefined) return;
  if (value === null) {
    delete address[key];
    return;
  }
  address[key] = value;
}

/** Append-only: new references go after the stored identifiers. */
function nextIdentifiers(
  identifiers: Identifier[] | undefined,
  append: ExternalReference[] | undefined,
): Identifier[] | undefined {
  const stored = readExternalReferences(identifiers);
  const added = newExternalReferences(stored, append);
  if (!added.length) return identifiers;
  return [...(identifiers ?? []), ...added.map(toExternalReferenceIdentifier)];
}
